import { FormGroup, IconButton, TextField, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import styled from 'styled-components';
import { InputSubTitle, InputTitle } from './styles';
import { toast } from 'react-toastify';

const MAX_FILE_SIZE_NUMBER = 2; // 2MB
const MAX_FILE_SIZE = MAX_FILE_SIZE_NUMBER * 1024 * 1024; 
const HiddenInput = styled.input`
  display: none;
`;

const Container = styled.div`
  border: 1px dashed #1570EF;
  background-color: #f5f8fd;
`;

const UploadContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px;
  height: 54px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  border-radius: 4px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};

  &:hover {
    border-color: ${props => props.disabled ? 'inherit' : '#1976d2'};
  }
`;

const FileName = styled(Typography)`
  flex-grow: 1;
  margin-left: 8px;
`;

export default function FileUpload({
  questionCode,
  label,
  description,
  onChange,
  onRemove,
  disabled,
}: {
  questionCode: number,
  label: string,
  description?: string,
  onChange?: (fileContentValue: string, fileNameValue: string) => void,
  onRemove?: () => void,
  disabled?: boolean,
}) {
  const [fileName, setFileName] = useState<string>('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const textFieldRef = React.useRef<any>(null);
  const [fileError, setFileError] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  
  const checkFileType = (fileType: string): boolean => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];

    const fileTypeIsInvalid = !validTypes.includes(fileType);
    if (fileTypeIsInvalid) {
      toast.error('Formato de arquivo inválido. Somente PNG, JPEG, JPG ou PDF são aceitos.');
      setFileError('Formato de arquivo inválido. Somente PNG, JPEG, JPG ou PDF são aceitos.');
      setFileName('');
      setTimeout(() => setFileError(''), 10000);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return false;
    }

    return true;
  }

  const checkFileSize = (fileSize: number): boolean => {
    const fileIsLarge = fileSize > MAX_FILE_SIZE;
    if (fileIsLarge) {
      toast.error(`O arquivo é muito grande. O tamanho máximo permitido é de ${MAX_FILE_SIZE_NUMBER}MB.`);
      setFileError(`O arquivo é muito grande. O tamanho máximo permitido é de ${MAX_FILE_SIZE_NUMBER}MB.`);
      setFileName('');
      setTimeout(() => setFileError(''), 10000);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return false;
    }

    return true;
  }

  const checkFile = (file: File) => {
    const fileTypeIsValid = checkFileType(file.type);
    if (!fileTypeIsValid) return false;

    const fileSizeIsValid = checkFileSize(file.size);
    if (!fileSizeIsValid) return false;

    return true;
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const newFiles = Array.from(event.target.files);
      const validFiles = newFiles.filter(file => checkFile(file));
      
      setFiles(prevFiles => [...prevFiles, ...validFiles]);
      const newFileNames = validFiles.map(file => file.name);
      setFileNames(prevNames => [...prevNames, ...newFileNames]);
      setFileError('');
  
      // Atualizar o TextField oculto
      if (textFieldRef.current) {
        textFieldRef.current.value = newFileNames.join(', ');
      }
  
      if (onChange) {
        validFiles.forEach(file => {
          const reader = new FileReader();
          reader.onload = (event) => {
            const fileContentValue = event.target?.result as string;
            onChange(fileContentValue.split(',')[1], file.name);
          };
          reader.readAsDataURL(file);
        });
      }
    }
  };
  
  const handleRemoveAllFiles = (e: React.MouseEvent) => {
    setFiles([]);
    setFileNames([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (textFieldRef.current) textFieldRef.current.value = '';
    e.stopPropagation();
    e.preventDefault();
    
    if (onRemove) {
      onRemove();
    }
  };
   
  
  return (
    <>
      <InputTitle>{label}</InputTitle>
      {description && (<InputSubTitle>{description}</InputSubTitle>)}
      <Container>
        <label htmlFor={"file-upload-" + questionCode}>
          <FormGroup>
            <HiddenInput
              accept=".png, .jpg, .jpeg, .pdf"
              id={"file-upload-" + questionCode}
              type="file"
              onChange={handleFileChange}
              ref={fileInputRef}
              multiple
              disabled={disabled}
            />
          </FormGroup>
          <UploadContainer disabled={disabled}>
            {fileNames.length > 0 ? (
              <>
                <IconButton size="small" onClick={handleRemoveAllFiles} disabled={disabled}>
                  x
                </IconButton>
                <FileName variant="body1">
                  {fileNames.join(', ')}
                </FileName>
              </>
            ) : (
              <Typography variant="body2" color={disabled ? "textSecondary" : "initial"}>
                Clique para adicionar arquivos (Somente PNG, JPEG, JPG ou PDF)
              </Typography>
            )}
          </UploadContainer>
        </label>
      </Container>
      <TextField
        inputRef={textFieldRef}
        style={{ width: '1px', height: '1px', padding: 0, margin: 0, border: 0, top: '-60px', right: '-100px', opacity: 0 }}
        onFocus={(e) => e.target.blur()}
        disabled={disabled}
      />
      {fileError && (
        <Typography color="error" variant="body2" style={{ marginTop: '8px' }}>
          {fileError}
        </Typography>
      )}
    </>
  );  
}
